/**
 * DataTree — renders the four-row preview tree on the property-file page:
 * Energy performance, Planning history, Flood risk, Tax & regulation.
 *
 * Per redesign decision v1.2 item 3: EPC is the single FREE section (full
 * preview, [free] pill). The other three render one real headline row +
 * counter. Locked rows have NO price pill — the CTA card below the tree
 * is the sole price anchor (per the 2026-05-28 UX micro-audit, three
 * repeating £29 pills implied a per-row tariff). Locked rows use a
 * neutral "In full report" right-side marker instead.
 *
 * Per redesign decision v1.2 item 4: the Title row is omitted entirely
 * until HMLR is integrated.
 *
 * `available: false` on a PreviewSection is the section-fetch-failed
 * sentinel — render the row as "Not yet available" muted slate-700 text
 * with no marker (Property 39 — section independence preserved on the
 * free path).
 *
 * Empty section (available, headline=null, moreCount=0): render "No
 * records found in public sources" with no marker. The user has the
 * whole story; charging £29 for an empty section is the worst trust
 * signal.
 *
 * EPC is rendered as `Band <current> now → potential <potential>`. If
 * `sections.epc` is undefined, the EPC row is omitted entirely (we don't
 * have data for this property — distinct from "fetch failed").
 */

import type { BuyerReportEpcSection, PreviewSection, PropertyFilePreview } from '@property-intel/shared';

interface DataTreeProps {
  sections: PropertyFilePreview;
}

function LockedMarker() {
  return (
    <span
      className="text-xs font-medium text-slate-700 whitespace-nowrap"
      aria-label="Included in the Buyer's Report"
    >
      In full report
    </span>
  );
}

function FreePill() {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
      aria-label="Free section"
    >
      free
    </span>
  );
}

function EpcRow({ epc }: { epc: BuyerReportEpcSection }) {
  const headline = `Band ${epc.currentRating} now → potential ${epc.potentialRating}`;
  return (
    <li className="border-b border-slate-200 py-4" data-testid="datatree-row-epc">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-950">Energy performance</p>
          <p className="mt-1 text-sm text-slate-700">{headline}</p>
          <p className="mt-1 text-sm text-slate-700">View full EPC</p>
        </div>
        <FreePill />
      </div>
    </li>
  );
}

interface PreviewRowProps {
  section: PreviewSection;
  title: string;
  /** Fallback label when there's a headline but no extra rows (moreCount=0). */
  fullViewLabel: string;
  /** Plural form, e.g. "records", "risk indicators", "flags". */
  unitNoun: string;
  /** Singular form, used when moreCount === 1. */
  unitNounSingular: string;
  testId: string;
}

function PreviewRow({ section, title, fullViewLabel, unitNoun, unitNounSingular, testId }: PreviewRowProps) {
  // available: false → muted "Not yet available" row, no marker
  if (!section.available) {
    return (
      <li className="border-b border-slate-200 py-4" data-testid={testId}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-700">{title}</p>
            <p className="mt-1 text-sm text-slate-700">Not yet available</p>
          </div>
        </div>
      </li>
    );
  }

  // available + no headline + no extra rows → genuine empty state, no marker
  if (section.headline === null && section.moreCount === 0) {
    return (
      <li className="border-b border-slate-200 py-4" data-testid={testId}>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-950">{title}</p>
          <p className="mt-1 text-sm text-slate-700">No records found in public sources</p>
        </div>
      </li>
    );
  }

  const headline = section.headline;
  const more = section.moreCount > 0
    ? `${section.moreCount} more ${section.moreCount === 1 ? unitNounSingular : unitNoun}`
    : fullViewLabel;

  return (
    <li className="border-b border-slate-200 py-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-950">{title}</p>
          {headline && <p className="mt-1 text-sm text-slate-700">{headline}</p>}
          <p className="mt-1 text-sm text-slate-700">{more}</p>
        </div>
        <LockedMarker />
      </div>
    </li>
  );
}

export default function DataTree({ sections }: DataTreeProps) {
  return (
    <section aria-labelledby="datatree-heading" className="mt-8">
      <h2 id="datatree-heading" className="text-lg font-semibold text-slate-950">
        Public records for this property
      </h2>
      <ul className="mt-4 border-t border-slate-200" data-testid="datatree">
        {sections.epc && <EpcRow epc={sections.epc} />}
        <PreviewRow
          section={sections.planning}
          title="Planning history"
          fullViewLabel="Full planning history"
          unitNoun="records"
          unitNounSingular="record"
          testId="datatree-row-planning"
        />
        <PreviewRow
          section={sections.flood}
          title="Flood risk"
          fullViewLabel="Full risk profile"
          unitNoun="risk indicators"
          unitNounSingular="risk indicator"
          testId="datatree-row-flood"
        />
        <PreviewRow
          section={sections.taxFlags}
          title="Tax & regulation"
          fullViewLabel="Full flag list"
          unitNoun="flags"
          unitNounSingular="flag"
          testId="datatree-row-tax"
        />
      </ul>
    </section>
  );
}
